/**
 * InterestCostCard — "The cost of your debt" (Phase 86.F).
 *
 * Interest paid this year, interest paid since a debt began (only when the
 * history can carry it), and interest still to come (always an estimate).
 * Recorded and calculated amounts are shown side by side but never added
 * together, and currencies are never combined. Wording is "interest paid" —
 * principal is paid off, interest is paid.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { Receipt, Info } from 'lucide-react';
import { formatMoney } from '@/lib/format';

const REASON_COPY = {
  no_interest_rate: 'no interest rate recorded',
  no_monthly_payment: 'no monthly payment recorded',
  payment_below_interest: 'its payment doesn\u2019t cover the interest',
  no_start_date: 'no start date recorded',
  no_original_principal: 'the original amount isn\u2019t recorded',
  incomplete_payment_history: 'the payment history isn\u2019t complete',
};

function calcDate(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
  } catch { return null; }
}

function Pill({ children }) {
  return (
    <span className="inline-block text-[10px] uppercase tracking-wide text-muted-foreground border border-border rounded-full px-2 py-0.5 mt-2">
      {children}
    </span>
  );
}

function SummaryCell({ label, value, note, chip, muted, testid }) {
  return (
    <div className="p-4" data-testid={testid}>
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className={`font-display mt-0.5 leading-tight ${muted ? 'text-lg text-charcoal' : 'text-2xl text-charcoal'}`}>
        {value}
      </p>
      {note && <p className="text-xs text-muted-foreground mt-1">{note}</p>}
      {chip && <Pill>{chip}</Pill>}
    </div>
  );
}

function DebtRow({ row, saving, year }) {
  const ty = row.this_year;
  const traceable = ty.recorded_payments > 0 && row.trace?.search;
  const paid = ty.recorded_amount > 0 ? ty.recorded_amount : ty.estimated_amount;
  const paidLabel = ty.recorded_amount > 0
    ? `Interest recorded in ${year}`
    : (ty.estimated_amount > 0 ? `Estimated interest in ${year}` : `Nothing recorded for ${year}`);

  return (
    <li className="px-4 py-3.5 grid gap-2 sm:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)_minmax(0,1fr)] sm:items-baseline"
        data-testid={`interest-debt-${row.debt_id}`}>
      <div className="min-w-0">
        <p className="text-sm font-medium text-charcoal truncate">{row.name}</p>
        <p className="text-xs text-muted-foreground">
          {(row.kind || '').replace(/_/g, ' ') || 'Debt'}
          {row.apr ? ` · ${row.apr}% APR` : ' · no rate on record'}
        </p>
      </div>

      <div className="sm:text-right">
        <p className="text-sm text-charcoal tabular-nums"
           data-testid={ty.recorded_amount > 0
             ? `interest-debt-recorded-${row.debt_id}`
             : `interest-debt-estimated-${row.debt_id}`}>
          {paid > 0 ? formatMoney(paid, row.currency) : '—'}
        </p>
        <p className="text-xs text-muted-foreground">{paidLabel}</p>
        {ty.recorded_amount > 0 && ty.estimated_amount > 0 && (
          <p className="text-xs text-muted-foreground">
            + {formatMoney(ty.estimated_amount, row.currency)} estimated for{' '}
            {ty.estimated_months} month{ty.estimated_months === 1 ? '' : 's'}
          </p>
        )}
        {traceable && (
          <Link
            to={`/transactions?q=${encodeURIComponent(row.trace.search)}&from=${year}-01-01&category=Loan%20Payment`}
            className="text-xs text-evergreen hover:underline"
            data-testid={`interest-debt-trace-${row.debt_id}`}
          >
            See payment breakdown
          </Link>
        )}
      </div>

      <div className="sm:text-right">
        <p className="text-sm text-charcoal tabular-nums">
          {row.remaining?.available
            ? `${formatMoney(row.remaining.amount, row.currency)} remaining`
            : 'Remaining not available'}
        </p>
        {saving?.interest_saved ? (
          <p className="text-xs text-evergreen" data-testid={`interest-debt-saving-${row.debt_id}`}>
            {formatMoney(saving.extra_monthly, row.currency)} extra could save{' '}
            {formatMoney(saving.interest_saved, row.currency)}
          </p>
        ) : (
          <p className="text-xs text-muted-foreground">
            {(row.this_year.source_labels || [])[0] || 'Incomplete'}
          </p>
        )}
      </div>
    </li>
  );
}

function CurrencyBlock({ group, savings, showCurrency }) {
  const ty = group.this_year;
  const recorded = ty.recorded_amount > 0;
  const lifetime = group.lifetime;
  const remaining = group.remaining;

  return (
    <div className="mt-4 first:mt-0" data-testid={`interest-currency-${group.currency}`}>
      {showCurrency && (
        <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-medium mb-2">
          {group.currency} debts
        </p>
      )}

      <div className="rounded-xl border border-border overflow-hidden bg-background/40">
        <div className="grid sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-border">
          <SummaryCell
            testid={`interest-year-${group.currency}`}
            label={recorded ? `Interest paid in ${group.year}` : `Estimated interest paid in ${group.year}`}
            value={formatMoney(recorded ? ty.recorded_amount : ty.estimated_amount, group.currency)}
            note={recorded
              ? `Recorded from ${ty.recorded_payments} statement payment${ty.recorded_payments === 1 ? '' : 's'}${
                  ty.estimated_amount > 0
                    ? `, plus ${formatMoney(ty.estimated_amount, group.currency)} estimated for ${ty.estimated_months} more`
                    : ''}`
              : `${ty.estimated_months} payment-month${ty.estimated_months === 1 ? '' : 's'} represented`}
            chip={recorded ? 'Recorded from statement' : 'Calculated from loan terms'}
          />
          <SummaryCell
            testid={`interest-remaining-${group.currency}`}
            label="Estimated interest still to come"
            muted={!remaining.available}
            value={remaining.available
              ? formatMoney(remaining.amount, group.currency)
              : 'Not available'}
            note={remaining.available
              ? 'Assumes today\u2019s rate and payment continue'
              : 'Needs a rate and a payment on record'}
            chip={remaining.available ? 'Estimated' : null}
          />
          <SummaryCell
            testid={lifetime.complete && lifetime.recorded_amount > 0
              ? `interest-lifetime-${group.currency}`
              : `interest-lifetime-unavailable-${group.currency}`}
            label="Since these debts began"
            muted={!(lifetime.complete && lifetime.recorded_amount > 0)}
            value={lifetime.complete && lifetime.recorded_amount > 0
              ? formatMoney(lifetime.recorded_amount, group.currency)
              : 'Not available yet'}
            note={lifetime.complete && lifetime.recorded_amount > 0
              ? `From ${lifetime.recorded_payments} recorded payments`
              : 'Needs the original amount, a start date and a recorded payment history \u2014 not just today\u2019s balance'}
            chip={lifetime.complete && lifetime.recorded_amount > 0 ? 'Recorded from statement' : 'Missing history'}
          />
        </div>

        <ul className="divide-y divide-border border-t border-border bg-card">
          {group.debts.map((row) => (
            <DebtRow key={row.debt_id} row={row} year={group.year}
                     saving={savings[row.debt_id]} />
          ))}
        </ul>
      </div>

      {remaining.debts_missing?.length > 0 && (
        <p className="text-xs text-muted-foreground mt-2">
          Not counted in the remaining estimate: {remaining.debts_missing
            .map((d) => `${d.name} (${REASON_COPY[d.reason] || d.reason})`).join(', ')}
        </p>
      )}
    </div>
  );
}

export function InterestCostCard({ interestCost, priorities }) {
  if (!interestCost?.available) return null;
  const groups = interestCost.currencies || [];
  if (groups.length === 0) return null;

  const savings = {};
  for (const r of (priorities?.avalanche || [])) {
    savings[r.debt_id] = r.extra_impact;
  }

  return (
    <div className="bg-card rounded-2xl border border-border p-5 shadow-sm mb-4"
         data-testid="interest-cost-card">
      <p className="font-display text-base md:text-lg text-charcoal leading-tight mb-1 flex items-center gap-2">
        <Receipt className="h-4 w-4 text-evergreen" aria-hidden="true" />
        The cost of your debt
      </p>
      <p className="text-xs text-muted-foreground">
        See how much of your repayments went to interest, and what could reduce the
        interest still ahead.
      </p>

      <div className="mt-3">
        {groups.map((g) => (
          <CurrencyBlock key={g.currency} group={g} savings={savings}
                         showCurrency={groups.length > 1} />
        ))}
      </div>

      <p className="text-[11px] text-muted-foreground mt-3 flex items-start gap-1.5"
         data-testid="interest-cost-footnote">
        <Info className="h-3 w-3 mt-0.5 shrink-0" aria-hidden="true" />
        <span>
          Recorded and calculated amounts are kept separate, and currencies are never
          added together. Calculated {calcDate(interestCost.calculated_at)} using the
          latest balance on record for each debt.
        </span>
      </p>
    </div>
  );
}

export default InterestCostCard;
